import { useNavigate } from 'react-router-dom';
import { showToastFromSnackbar } from '@/utils/showToast';
import { v4 as uuidv4 } from 'uuid';
import { useUserStore } from '@/stores/userStore';
import { useLocales } from '@/locales';
import { useApiMutation } from '@/hooks/useApi';
import authApi from '@/api/authApi';
import { AUTH_ROUTES, USER_ROLES } from '@/pages/auth/constants/authConstants';
import { useAuthFlowStore } from '@/pages/auth/stores/authFlowStore';
import {
  validateAdminForm,
  checkRateLimit,
} from '@/pages/auth/utils/validation';

/**
 * Custom hook for admin authentication
 */
export const useAdminAuth = () => {
  const navigate = useNavigate();

  const { translate } = useLocales();
  const setUser = useUserStore(state => state.setUser);

  // Auth flow store integration
  const {
    adminState,
    setAdminCredentials,
    setAdminErrors,
    setAdminLoading,
    resetAdminState,
    getCanSubmitAdmin,
    addAuthAttempt,
  } = useAuthFlowStore();

  const {
    credentials,
    errors,
    isLoading: storeLoading,
    lastSubmissionTime,
  } = adminState;

  // API mutation for login
  const loginMutation = useApiMutation(
    ({ email, password }) => authApi.login({ email, password }),
    {
      publishErrors: false,
      onSuccess: result => {
        handleLoginSuccess(result);
      },
      onError: error => {
        handleLoginError(error);
      },
    },
  );

  const handleCredentialChange = field => event => {
    const value = event.target.value;
    setAdminCredentials(field, value);

    // Clear any previous errors
    if (loginMutation.error) {
      loginMutation.reset();
    }
  };

  const togglePasswordVisibility = () => {
    setAdminCredentials('showPassword', !credentials.showPassword);
  };

  const validateForm = () => {
    const validation = validateAdminForm(credentials);

    if (!validation.isValid) {
      setAdminErrors(validation.fieldErrors);
    }

    return validation.isValid;
  };

  const handleLoginSuccess = result => {
    // Reset loading state
    setAdminLoading(false);

    // Record successful attempt
    addAuthAttempt({
      type: 'admin',
      success: true,
      email: credentials.email,
    });

    // Reset form
    resetAdminState();

    // Create user object
    const user = {
      id: result.data._id,
      role: result.data.role,
      isVerified: true,
      details: result.data,
      token: result.token,
      socketUUID: uuidv4(),
    };

    setUser(user);

    // Show success message
    showToastFromSnackbar(translate('Login_Successful'), {
      variant: 'success',
    });

    // Navigate based on role
    setTimeout(() => {
      if (result.data.role === USER_ROLES.OPERATOR) {
        navigate(AUTH_ROUTES.CALL_CENTER);
      } else {
        navigate(AUTH_ROUTES.DASHBOARD);
      }
    }, 1000);
  };

  const handleLoginError = error => {
    // Reset loading state
    setAdminLoading(false);

    // Record failed attempt
    addAuthAttempt({
      type: 'admin',
      success: false,
      email: credentials.email,
      error: error.message,
    });

    const details = error.details || {};
    const fieldErrors = {
      email: !!details.email,
      password: !!details.password,
    };

    if (fieldErrors.email || fieldErrors.password) {
      setAdminErrors(fieldErrors);
    } else {
      setAdminErrors({ email: true, password: true });
    }

    showToastFromSnackbar(
      error.message || translate('Check_email_address_password_again'),
      { variant: 'error' },
    );
  };

  const handleSubmit = async event => {
    event.preventDefault();

    // Check rate limiting
    const rateLimitCheck = checkRateLimit(lastSubmissionTime);
    if (!rateLimitCheck.allowed) {
      showToastFromSnackbar(translate('Please wait before trying again'), {
        variant: 'warning',
      });
      return;
    }

    if (!validateForm()) {
      showToastFromSnackbar(translate('message_7'), { variant: 'warning' });
      return;
    }

    const { email, password } = credentials;
    setAdminLoading(true);
    loginMutation.mutate({ email, password });
  };

  const resetForm = () => {
    resetAdminState();
    loginMutation.reset();
  };

  return {
    // State
    credentials,
    errors,
    isLoading: loginMutation.isPending || storeLoading,
    error: loginMutation.error,
    canSubmit: getCanSubmitAdmin() && !loginMutation.isPending,
    rateLimitInfo: checkRateLimit(lastSubmissionTime),

    // Actions
    handleCredentialChange,
    togglePasswordVisibility,
    handleSubmit,
    resetForm,
  };
};
